import { useState } from 'react';

import type { useToast } from '../../../context/ToastContext';
import { userProfileAPI } from '../../../services/api';
import { trackAuthEvent } from '../../../services/analytics';
import { confirmDialog, showDialog } from '../../../utils/dialogs';
import {
  completeAccountDeletion,
  deleteAccountErrorKey,
  deleteAccountErrorType,
} from '../accountDeletion';
import type { DeleteAccountForm } from '../modals/DeleteAccountModal';
import type { EditProfileForm } from '../modals/EditProfileModal';
import type { Translate, UserProfile } from '../profileTypes';

interface UseAccountFormsOptions {
  profile: UserProfile | null;
  loadProfile: () => Promise<void>;
  logout: () => Promise<void>;
  showToast: ReturnType<typeof useToast>['showToast'];
  t: Translate;
}

/**
 * Edit-profile and delete-account modals. Both share one `formLoading` flag,
 * as they always have.
 */
export function useAccountForms({ profile, loadProfile, logout, showToast, t }: UseAccountFormsOptions) {
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [editForm, setEditForm] = useState<EditProfileForm>({ full_name: '', email: '' });
  const [deleteForm, setDeleteForm] = useState<DeleteAccountForm>({ confirmation: '', reason: '' });
  const [formLoading, setFormLoading] = useState(false);

  /** Re-seeds the edit form from a freshly loaded profile. */
  const syncEditForm = (data: UserProfile) => {
    setEditForm({
      full_name: data.full_name || '',
      email: data.email || '',
    });
  };

  const openEditProfile = () => {
    trackAuthEvent('profile_edit_opened');
    setShowEditProfile(true);
  };

  const openDeleteAccount = () => {
    trackAuthEvent('account_delete_modal_opened');
    setShowDeleteAccount(true);
  };

  const handleEditProfile = async () => {
    if (!editForm.full_name.trim()) {
      showDialog(t('common.error'), t('profile.enterFullName'));
      return;
    }

    setFormLoading(true);
    try {
      const nameChanged = editForm.full_name.trim() !== profile?.full_name;
      const emailChanged = editForm.email !== profile?.email;
      await userProfileAPI.updateProfile({
        full_name: editForm.full_name.trim(),
        email: editForm.email !== profile?.email ? editForm.email : undefined,
      });

      trackAuthEvent('profile_updated', {
        name_changed: nameChanged,
        email_changed: emailChanged,
      });
      showToast(t('profile.profileUpdated'), 'success');
      setShowEditProfile(false);
      await loadProfile();
    } catch (error: any) {
      const errorStatus = error?.response?.status;
      const errorType = errorStatus ? `http_${errorStatus}` : (error?.message ? 'exception' : 'unknown');
      trackAuthEvent('profile_update_failed', { error_type: errorType });
      const message = error.response?.data?.detail || t('profile.updateFailed');
      showDialog(t('common.error'), message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    // The authenticated session, typed DELETE confirmation, rate limit, and
    // final destructive confirmation gate this action. Passwordless accounts
    // do not have a password the user can provide here.
    if (deleteForm.confirmation !== 'DELETE') {
      showDialog(t('profile.deleteConfirmError'));
      return;
    }

    trackAuthEvent('account_delete_initiated');
    const confirmed = await confirmDialog(
      t('profile.finalConfirmation'),
      t('profile.finalConfirmationMessage'),
      t('profile.deleteAccount'),
      t('common.cancel'),
    );
    if (!confirmed) return;

    setFormLoading(true);
    try {
      await userProfileAPI.deleteAccount({
        confirmation: deleteForm.confirmation,
        reason: deleteForm.reason,
      });
    } catch (error: unknown) {
      // Deletion is one server transaction: an error response means the account
      // was not deleted (a missing response means the outcome is unknown).
      trackAuthEvent('account_delete_failed', { error_type: deleteAccountErrorType(error) });
      showDialog(t('common.error'), t(deleteAccountErrorKey(error)));
      setFormLoading(false);
      return;
    }

    trackAuthEvent('account_deleted', {
      has_reason: Boolean(deleteForm.reason?.trim()),
    });
    // The session is dead: log out directly, with no confirm/cancel step (ADM-15).
    await completeAccountDeletion({
      closeDeleteModal: () => setShowDeleteAccount(false),
      notifyDeleted: () => showDialog(t('profile.accountDeleted'), t('profile.accountDeletedMessage')),
      logout,
    });
  };

  return {
    showEditProfile,
    closeEditProfile: () => setShowEditProfile(false),
    openEditProfile,
    editForm,
    setEditForm,
    handleEditProfile,
    showDeleteAccount,
    closeDeleteAccount: () => setShowDeleteAccount(false),
    openDeleteAccount,
    deleteForm,
    setDeleteForm,
    handleDeleteAccount,
    formLoading,
    syncEditForm,
  };
}
